import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import ReportVisualization from './ReportVisualization';
import { useToast } from '../../contexts/ToastContext';

/**
 * Shared Report Center visualization dialog.
 * Any report whose preview payload matches serializeReportPreviewPayload
 * (table sections with columns/rows) can open this dialog — including
 * future table-backed reports.
 */
function collectVisualizationSvgs(container, title) {
  if (!container) return [];
  const nodes = Array.from(container.querySelectorAll('svg'));
  const serializer = new XMLSerializer();
  return nodes
    .map((node, index) => {
      let svg = serializer.serializeToString(node);
      if (!svg.includes('xmlns=')) {
        svg = svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      return {
        title: `${title || 'Report'} - Chart ${index + 1}`,
        svg
      };
    })
    .filter((item) => item.svg.includes('<svg'));
}

function sanitizeFilenameBase(title) {
  return String(title || 'visualization')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'visualization';
}

export default function ReportVisualizeDialog({
  open,
  title = 'Report',
  data = null,
  downloading = false,
  onClose,
  onDownloadPdfWithVisualization
}) {
  const { showToast } = useToast();
  const contentRef = useRef(null);
  const [includeInPdf, setIncludeInPdf] = useState(false);
  const [localDownloading, setLocalDownloading] = useState(false);

  const busy = downloading || localDownloading;
  const canAppendToPdf = typeof onDownloadPdfWithVisualization === 'function';

  const handleClose = () => {
    setIncludeInPdf(false);
    onClose?.();
  };

  const handleDownloadPng = useCallback(async () => {
    if (!contentRef.current || !data) {
      showToast('No visualization available to download', 'warning');
      return;
    }
    setLocalDownloading(true);
    try {
      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        width: element.scrollWidth,
        height: element.scrollHeight,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl;
      link.setAttribute('download', `${sanitizeFilenameBase(title)}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Visualization PNG downloaded', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to download visualization PNG', 'error');
    } finally {
      setLocalDownloading(false);
    }
  }, [data, showToast, title]);

  const handleDownloadPdfWithViz = useCallback(async () => {
    if (!canAppendToPdf) return;
    const visualizations = collectVisualizationSvgs(contentRef.current, title);
    if (!visualizations.length) {
      showToast('No charts found to append', 'warning');
      return;
    }
    await onDownloadPdfWithVisualization(visualizations);
  }, [canAppendToPdf, onDownloadPdfWithVisualization, showToast, title]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Visualize — {title}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="close visualization"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default' }} ref={contentRef}>
        {data ? (
          <ReportVisualization data={data} />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {canAppendToPdf && (
          <FormControlLabel
            control={(
              <Checkbox
                checked={includeInPdf}
                onChange={(e) => setIncludeInPdf(e.target.checked)}
              />
            )}
            label="Include visualizations in PDF"
          />
        )}
        <Box flexGrow={1} />
        <Button onClick={handleDownloadPng} disabled={!data || busy}>
          Download visualization
        </Button>
        {canAppendToPdf && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadPdfWithViz}
            disabled={!data || !includeInPdf || busy}
          >
            Download PDF + visualization
          </Button>
        )}
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
